import { useAtomValue, useSetAtom } from 'jotai';

import { LeadStageType, LeadType } from '@ekhodealer/ekho-common';
import { Stack } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import {
	FiltersDrawerCheckboxGroup,
	type FiltersDrawerCheckboxGroupItem,
} from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';
import { toSentenceCase } from '@common/lib/strings';

import {
	leadTypeFilterAtom,
	stageFilterAtom,
	updateLeadTypeFilterAtom,
	updateStageFilterAtom,
} from '../../atoms/leadsAtoms';
import { useLeadsFacetCounts } from '../../hooks/useLeadsFacetCounts';

interface FiltersDrawerStageTypeSectionProps {
	viewMode?: 'table' | 'board';
}

/**
 * FiltersDrawerStageTypeSection component that provides stage and type filtering options
 * wrapped in an accordion
 */
export function FiltersDrawerStageTypeSection({
	viewMode = 'table',
}: FiltersDrawerStageTypeSectionProps) {
	const stages = useAtomValue(stageFilterAtom);
	const leadTypes = useAtomValue(leadTypeFilterAtom);
	const setStages = useSetAtom(updateStageFilterAtom);
	const setLeadTypes = useSetAtom(updateLeadTypeFilterAtom);

	// Get dynamic facet counts from the search response
	const { stageCounts, typeCounts } = useLeadsFacetCounts();

	// Build stage items with dynamic counts
	const stageItems: FiltersDrawerCheckboxGroupItem[] = Object.values(LeadStageType).map(
		(stage) => ({
			value: stage,
			label: toSentenceCase(stage),
			count: stageCounts[stage] ?? 0,
		}),
	);

	// Build type items with dynamic counts
	const typeItems: FiltersDrawerCheckboxGroupItem[] = Object.values(LeadType).map((type) => ({
		value: type,
		label: toSentenceCase(type),
		count: typeCounts[type] ?? 0,
	}));

	// Determine if this section has any applied filters
	const hasAppliedFilters = stages.length > 0 || leadTypes.length > 0;

	// Conditional title based on view mode
	const sectionTitle = viewMode === 'board' ? 'Type' : 'Stage and type';

	return (
		<AccordionSection title={sectionTitle} defaultExpanded={hasAppliedFilters}>
			<Stack itemsSpacing='24'>
				{viewMode === 'table' && (
					<FiltersDrawerCheckboxGroup
						label='Stage'
						items={stageItems}
						value={stages}
						onChange={setStages}
					/>
				)}
				<FiltersDrawerCheckboxGroup
					label='Type'
					items={typeItems}
					value={leadTypes}
					onChange={setLeadTypes}
				/>
			</Stack>
		</AccordionSection>
	);
}
